import { flow, types } from "mobx-state-tree";
import { z } from "zod";
import { getEnv } from "~/mobx/utils/getEnv";
import { getRoot } from "~/mobx/utils/getRoot";
import type { AppointmentInstance } from "../appointment/Appointment";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface BookingStoreInstance extends Instance<typeof BookingStore> {}
export interface BookingStoreSnapshotIn
  extends SnapshotIn<typeof BookingStore> {}
export interface BookingStoreSnapshotOut
  extends SnapshotOut<typeof BookingStore> {}

export const BookingStore = types
  .model("BookingStore", {
    clinicId: types.maybeNull(types.string),
    reasonId: types.maybeNull(types.string),

    patientId: types.maybeNull(types.string), //  "000000" is set when user is creating a new pet

    practitionerId: types.maybeNull(types.string),
    start: types.maybeNull(types.string),
    end: types.maybeNull(types.string),
    clientNotes: types.maybeNull(types.string),
    isTosAccepted: types.optional(types.boolean, false),

    // fields for new pet if its a new user
    newPetName: types.maybeNull(types.string),
    newPetSpeciesId: types.maybeNull(types.string),

    // field for new client if its a new user
    newClientFirstName: types.maybeNull(types.string),
    newClientLastName: types.maybeNull(types.string),
    newClientStreetAddress: types.maybeNull(types.string),
    newClientZipCode: types.maybeNull(types.string),
    newClientCity: types.maybeNull(types.string),
    newClientEmail: types.maybeNull(types.string),
    newClientPhoneNumber: types.maybeNull(types.string),

    // fields for old client missing details
    existingClientEmail: types.maybe(types.string),
    existingClientPhoneNumber: types.maybe(types.string),
    existingClientStreetAddress: types.maybe(types.string),
    existingClientZipCode: types.maybe(types.string),
    existingClientCity: types.maybe(types.string),

    // ui related fields
    currentProgressBarStep: types.optional(types.number, 1),
    totalProgressBarSteps: types.optional(types.number, 8),
    showHeader: types.optional(types.boolean, true),
    patientSpeciesId: types.maybeNull(types.string),

    // fields for tracking / gtm
    appointmentId: types.maybeNull(types.string),
    clinicName: types.maybeNull(types.string),
  })
  .views((self) => {
    return {
      get existingClientHasNewDetails() {
        return (
          !!self.existingClientEmail ||
          !!self.existingClientPhoneNumber ||
          !!self.existingClientStreetAddress ||
          !!self.existingClientZipCode ||
          !!self.existingClientCity
        );
      },

      get hasPatientIdOrNewPet() {
        return self.patientId || (self.newPetName && self.newPetSpeciesId);
      },

      get hasNewUserValues() {
        return (
          !!self.newClientFirstName &&
          !!self.newClientLastName &&
          !!self.newClientStreetAddress &&
          !!self.newClientZipCode &&
          !!self.newClientCity &&
          !!self.newClientEmail &&
          !!self.newClientPhoneNumber
        );
      },

      get hasNewPetValues() {
        return !!self.newPetName && !!self.newPetSpeciesId;
      },
    };
  })

  .actions((self) => {
    return {
      setNewPet({
        newPetName,
        newPetSpeciesId,
      }: {
        newPetName: string;
        newPetSpeciesId: string;
      }) {
        self.newPetName = newPetName;
        self.newPetSpeciesId = newPetSpeciesId;
      },

      setExistingClientDetails({
        email,
        phoneNumber,
        streetAddress,
        zipCode,
        city,
      }: {
        email?: string;
        phoneNumber?: string;
        streetAddress?: string;
        zipCode?: string;
        city?: string;
      }) {
        if (email) {
          self.existingClientEmail = email;
        }

        if (phoneNumber) {
          self.existingClientPhoneNumber = phoneNumber;
        }

        if (streetAddress) {
          self.existingClientStreetAddress = streetAddress;
        }

        if (zipCode) {
          self.existingClientZipCode = zipCode;
        }

        if (city) {
          self.existingClientCity = city;
        }
      },

      setNewClientDetails({
        newClientFirstName,
        newClientLastName,
        newClientStreetAddress,
        newClientZipCode,
        newClientCity,
        newClientEmail,
        newClientPhoneNumber,
      }: {
        newClientFirstName: string;
        newClientLastName: string;
        newClientStreetAddress: string;
        newClientZipCode: string;
        newClientCity: string;
        newClientEmail: string;
        newClientPhoneNumber: string;
      }) {
        self.newClientFirstName = newClientFirstName;
        self.newClientLastName = newClientLastName;
        self.newClientStreetAddress = newClientStreetAddress;
        self.newClientZipCode = newClientZipCode;
        self.newClientCity = newClientCity;
        self.newClientEmail = newClientEmail;
        self.newClientPhoneNumber = newClientPhoneNumber;
      },

      setClinicId(clinicId: string) {
        self.clinicId = clinicId;
      },

      setReasonId(reasonId: string) {
        self.reasonId = reasonId;
      },

      setPatientId(petId: string) {
        self.patientId = petId;
      },
      setPatientSpeciesId(speciesId: string | null) {
        self.patientSpeciesId = speciesId;
      },

      setTimeslot(start: string, end: string, practitionerId: string) {
        self.start = start;
        self.end = end;
        self.practitionerId = practitionerId;
      },

      setClientNotes(notes: string) {
        self.clientNotes = notes;
      },

      setIsTosAccepted(isAccepted: boolean) {
        self.isTosAccepted = isAccepted;
      },

      setAppointmentId(appointmentId: string) {
        self.appointmentId = appointmentId;
      },

      setClinicName(clinicName: string) {
        self.clinicName = clinicName;
      },

      resetBooking() {
        self.clinicId = null;
        self.reasonId = null;
        self.patientId = null;
        self.patientSpeciesId = null;
        self.newPetName = null;
        self.newPetSpeciesId = null;

        self.start = null;
        self.end = null;
        self.practitionerId = null;
        self.clientNotes = null;
        self.isTosAccepted = false;

        self.newClientFirstName = null;
        self.newClientLastName = null;
        self.newClientStreetAddress = null;
        self.newClientZipCode = null;
        self.newClientCity = null;
        self.newClientEmail = null;
        self.newClientPhoneNumber = null;

        self.appointmentId = null;
        self.clinicName = null;
      },

      bookAppointment: flow<AppointmentInstance, []>(
        function* bookAppointment(): any {
          const env = getEnv(self);
          const root = getRoot(self);

          const rawData = {
            clinicId: root.bookingStore.clinicId,
            reasonId: root.bookingStore.reasonId,
            practitionerId: root.bookingStore.practitionerId,
            startUtc: root.bookingStore.start,
            endUtc: root.bookingStore.end,
            patientId: root.bookingStore.patientId,
            clientNotes: root.bookingStore.clientNotes,
            isTosAccepted: root.bookingStore.isTosAccepted,
          };

          const schema = z
            .object({
              clinicId: z.string(),
              reasonId: z.string(),
              practitionerId: z.string(),
              startUtc: z.string(),
              endUtc: z.string(),
              patientId: z.string(),
              clientNotes: z.string(),
              isTosAccepted: z.boolean(),
            })
            .strict();

          const data = schema.parse(rawData);

          const response: AxiosResponse = yield env.baseAuthHttp.post(
            "appointments/",
            data,
          );

          const appointment = response.data.data;

          response.data = root.appointmentStore.processOne(appointment);

          return response.data;
        },
      ),

      setCurrentProgressBarStep(step: number) {
        self.currentProgressBarStep = step;
      },

      setTotalProgressBarSteps(steps: number) {
        self.totalProgressBarSteps = steps;
      },

      setShowHeader(show: boolean) {
        self.showHeader = show;
      },
    };
  });
